// Global keyboard shortcuts. Lives at the App level so go-to bindings work
// from anywhere. Page-specific bindings (j/k/w/p/n/e/a) attach inside the
// triage page and skip while the user is typing in a form field.

import { useEffect, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';

interface ShortcutOptions {
  onPalette: () => void;
  onHelp: () => void;
  onCloseOverlays: () => void;
}

export function useGlobalShortcuts({ onPalette, onHelp, onCloseOverlays }: ShortcutOptions) {
  const navigate = useNavigate();
  // Pending "g" chord — keep open for 1s after the first key.
  const [gPending, setGPending] = useState(false);

  useEffect(() => {
    if (!gPending) return;
    const t = setTimeout(() => setGPending(false), 1000);
    return () => clearTimeout(t);
  }, [gPending]);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      const inForm = isInsideEditable(e.target);
      // ⌘K / Ctrl+K always wins, even in forms — opens the palette.
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        onPalette();
        return;
      }
      // Escape always closes overlays.
      if (e.key === 'Escape') {
        onCloseOverlays();
        return;
      }
      if (inForm) return;

      // ? opens shortcut overlay.
      if (e.key === '?' || (e.shiftKey && e.key === '/')) {
        e.preventDefault();
        onHelp();
        return;
      }
      // / focuses the queue filter when not in a form.
      if (e.key === '/') {
        e.preventDefault();
        const el = document.querySelector<HTMLElement>('[data-shortcut-target="filter"]');
        el?.focus();
        return;
      }

      // "g X" chord — go-to.
      if (gPending) {
        setGPending(false);
        switch (e.key.toLowerCase()) {
          case 't': e.preventDefault(); navigate({ to: '/' }); return;
          case 'p': e.preventDefault(); navigate({ to: '/admin/verify' }); return; // Profiles screen lives in Step 8; reuse verify for now.
          case 'a': e.preventDefault(); navigate({ to: '/applications' }); return;
          case 's': e.preventDefault(); /* future: snapshot */ return;
          case 'f': e.preventDefault(); navigate({ to: '/finance' }); return;
          case 'v': e.preventDefault(); navigate({ to: '/admin/verify' }); return;
        }
        return;
      }
      if (e.key.toLowerCase() === 'g') {
        setGPending(true);
        return;
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [gPending, navigate, onCloseOverlays, onHelp, onPalette]);
}

export function isInsideEditable(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  const tag = target.tagName;
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true;
  if (target.isContentEditable) return true;
  return false;
}
